import type { ParentType } from '@risksmart-app/domain/src/types/consts';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import TreeModel from 'tree-model';

const tree = new TreeModel();

interface ObjectType {
  id: string;
}

// COI-683 — `risk_assessment_result_impact` is NOT node-backed: it carries no
// `a_node_insert_trigger`, so it never gets a `risksmart.node` row. This edge was
// therefore proposed for removal as a "phantom" — a declared grant no data could
// ever carry.
//
// It stays, because THIS TREE HAS TWO CONSUMERS and the second one needs it:
//
//   1. Permissions — `classifyLink` → `parent_child` → `linkItems`. Here the edge
//      is already inert: `risk_assessment_result_impact` has no `_parent` table,
//      so `insertParentChildLink` rejects the pair and every linked-items route
//      answers 400. No grant has ever been minted through it.
//   2. Reporting — `src/reporting/datasetRelationships.ts:23-28` calls
//      `isDescendant` to decide whether one dataset joins another as `child`,
//      `parent` or `sibling` in the report builder. Here the edge is CORRECT and
//      load-bearing: the FK genuinely exists, and dropping the edge silently
//      demotes the join to `sibling`.
//
// So removing it fixes nothing (the permission path already refuses the pair) and
// breaks something real (report joins). The same reasoning keeps
// `document_file → attestation_record` below.
//
// `db-graph.test.ts` records both as deliberately non-node-backed rather than
// letting a genuinely accidental phantom edge hide among them.
const RiskAssessmentResultHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.RiskAssessmentResult,
  children: [{ id: ParentTypes.RiskAssessmentResultImpact }],
};

const AssessmentResults: TreeModel.Model<ObjectType>[] = [
  { id: ParentTypes.TestResult },
  RiskAssessmentResultHierarchy,
  { id: ParentTypes.ObligationAssessmentResult },
  { id: ParentTypes.DocumentAssessmentResult },
];

// COI-683 — the oversight and internal-audit result families, the second- and
// third-line analogues of `AssessmentResults` above.
//
// Both are node-backed and both attach through a `_parent` table
// (`oversight_result_parent` / `internal_audit_result_parent`) whose shape is
// identical to `assessment_result_parent`: `(ResultType, ParentId, ParentType)`,
// i.e. dual parentage — the result hangs off BOTH the assessment that produced it
// and the entity it assessed. Live rows in `assessment_result_parent` confirm that
// shape (`test_result` under both `assessment` and `control`, and so on).
//
// Scope note: these are declared under the ASSESSMENT-side parents only
// (`oversight_assessment`, `internal_audit_report`), not additionally under
// Risk/Control/Obligation/Document the way `AssessmentResults` is. Adding the
// entity-side leg is a second, larger grant widening and wants its own evidence;
// see the audit note at the bottom of this file.
//
// `internal_audit_report_result` is deliberately ABSENT: it exists in
// `ParentTypes` but has no table, so it is not node-backed and would be another
// phantom edge.
const OversightResults: TreeModel.Model<ObjectType>[] = [
  { id: ParentTypes.ControlTestOversightResult },
  { id: ParentTypes.DocumentOversightResult },
  { id: ParentTypes.ObligationOversightResult },
  { id: ParentTypes.RiskControlledOversightResult },
  { id: ParentTypes.RiskUncontrolledOversightResult },
  { id: ParentTypes.ImpactOversightRating },
];

const InternalAuditResults: TreeModel.Model<ObjectType>[] = [
  { id: ParentTypes.ControlTestInternalAuditResult },
  { id: ParentTypes.DocumentInternalAuditResult },
  { id: ParentTypes.ObligationInternalAuditResult },
  { id: ParentTypes.RiskControlledInternalAuditResult },
  { id: ParentTypes.RiskUncontrolledInternalAuditResult },
  { id: ParentTypes.ImpactInternalAuditRating },
];

const ImpactHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Impact,
  children: [{ id: ParentTypes.ImpactRating }],
};

const ActionHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Action,
  children: [{ id: ParentTypes.ActionUpdate }],
};

const IssueHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Issue,
  children: [
    { id: ParentTypes.IssueUpdate },
    ActionHierarchy,
    { id: ParentTypes.Cause },
    { id: ParentTypes.Consequence },
    { id: ParentTypes.IssueAssessment },
  ],
};

const AssessmentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Assessment,
  children: [
    ...AssessmentResults,
    // COI-683: these two were bare `{ id: Issue }` / `{ id: Action }` LEAVES, so
    // an Assessment owner reached a linked Action but not its Updates, and a
    // linked Issue but not its Causes, Consequences, Updates or Assessment.
    //
    // That was accidental, not a decision. `isDescendant` resolves a type to the
    // FIRST pre-order DFS occurrence in the whole tree, so these leaves were
    // shadowed by the subtree declarations elsewhere — which is exactly why the
    // asymmetry stayed invisible. The database has always granted the wider set
    // (`assessment → action_update` rows exist), so this aligns the tree with
    // behaviour that already shipped rather than widening anything new.
    //
    // The canonical-subtree invariant test in `hierarchy.test.ts` now makes a
    // divergence like this fail loudly instead of hiding behind DFS order.
    IssueHierarchy,
    ActionHierarchy,
    { id: ParentTypes.AssessmentActivity },
  ],
};

const IndicatorHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Indicator,
  children: [{ id: ParentTypes.IndicatorResult }],
};

const ControlHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Control,
  children: [
    { id: ParentTypes.TestResult },
    IssueHierarchy,
    ActionHierarchy,
    IndicatorHierarchy,
  ],
};

const ControlGroupHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ControlGroup,
  children: [ControlHierarchy],
};

const ObligationChangeHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ObligationChange,
  children: [ActionHierarchy],
};

const ObligationHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Obligation,
  children: [
    ControlHierarchy,
    ...AssessmentResults,
    // COI-627: an Obligation Change owns the actions raised from it. V2's
    // `actions/post.ts` allow-list included `ObligationChange`, but here it was
    // a childless leaf, so `classifyLink` resolved ObligationChange→Action as
    // `sibling`: the action got a `linked_item` row instead of an
    // `action_parent` row, vanished from the Obligation Change's Actions tab,
    // and never inherited the parent's perm_v2 grants.
    ObligationChangeHierarchy,
    { id: ParentTypes.ObligationImpact },
    ActionHierarchy,
    IssueHierarchy,
  ],
};

const RiskHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Risk,
  children: [
    ControlHierarchy,
    ...AssessmentResults,
    ImpactHierarchy,
    { id: ParentTypes.Appetite },
    { id: ParentTypes.Acceptance },
    ActionHierarchy,
    IndicatorHierarchy,
  ],
};

const EntityHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Entity,
  children: [],
};

const ThirdPartyResponseHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ThirdPartyResponse,
};

const QuestionnaireTemplateVersionHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.QuestionnaireTemplateVersion,
  children: [ThirdPartyResponseHierarchy],
};

const QuestionnaireTemplateHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.QuestionnaireTemplate,
  children: [QuestionnaireTemplateVersionHierarchy],
};

const ThirdPartyHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ThirdParty,
  children: [
    ThirdPartyResponseHierarchy,
    ControlHierarchy,
    ActionHierarchy,
    IssueHierarchy,
  ],
};

const DocumentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Document,
  children: [
    ...AssessmentResults,
    ActionHierarchy,
    IssueHierarchy,
    // Documents (policies) own compliance/performance indicators the same
    // way risks and controls do. Without this entry, classifyLink() treats
    // Document↔Indicator as a sibling relationship and routes the link
    // through insertSiblingLinks instead of the well-tested
    // indicator_parent parent-table path, which caused linked indicators
    // to disappear from the Document's Linked Items tab (RSP-4075).
    IndicatorHierarchy,
    // COI-683 — `attestation_record` is not node-backed either, and this edge
    // stays for the same reason: inert for permissions, correct and load-bearing
    // for the report builder's dataset joins. See the note on
    // `RiskAssessmentResultHierarchy`.
    {
      id: ParentTypes.DocumentFile,
      children: [{ id: ParentTypes.AttestationRecord }],
    },
  ],
};

// RSP-5374: Issues can be raised under Internal Audit reports/entities and
// Compliance Monitoring assessments — these types are listed in
// `ALLOWED_ISSUE_PARENT_TYPES` (issue-repository.ts). Without these entries,
// classifyLink() resolves the link as `sibling`, the issue create processor's
// validation reads `link.SourceType` as `'issue'` and throws a misleading
// "Invalid parent type: issue" error, blocking creation under these parents.
// COI-683: the three below gain their result families and `assessment_activity`.
//
// `assessment_activity` is the same table that backs RCSAs, and V2's
// `assessment-activity/post.ts` / `put.ts` allow-list has ALWAYS accepted
// `InternalAuditReport` and `OversightAssessment` as activity parents — so these
// activities exist in production while the tree denied the edge. Note this is the
// type-level edge that made a `Risk → AssessmentActivity` edge unusable for
// COI-683: it cannot distinguish an RCSA activity from an Internal Audit one,
// which is why the risk-owner grant is minted from the `RiskId` COLUMN instead.
// Nothing here gives a risk owner a path in; the edge is from the IA/oversight
// parent downwards.
const InternalAuditReportHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.InternalAuditReport,
  children: [
    IssueHierarchy,
    ...InternalAuditResults,
    { id: ParentTypes.AssessmentActivity },
  ],
};

const InternalAuditEntityHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.InternalAuditEntity,
  children: [
    IssueHierarchy,
    InternalAuditReportHierarchy,
    { id: ParentTypes.AssessmentActivity },
  ],
};

const OversightAssessmentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.OversightAssessment,
  children: [
    IssueHierarchy,
    ...OversightResults,
    { id: ParentTypes.AssessmentActivity },
  ],
};

// COI-627: Asset was absent from the tree entirely, so every Asset link
// classified as `sibling`. V2's `actions/post.ts` and `issues/post.ts`
// allow-lists both included `Asset`, which made two live product routes dead in
// V3 — an action raised from an Asset silently got no `action_parent` row, and
// an issue raised from an Asset was rejected outright with a misleading
// "Invalid parent type: issue" (the create processor read `link.SourceType`
// off a sibling classification). Asset owns actions and issues only; V2 never
// allowed it as a parent for controls or indicators.
//
// COI-666 — this edge also widens PERMISSIONS, and that is BY DESIGN. Product
// ruling (COI-666, 2026-08-04): "Actions/issues should always be children —
// and it applies after the fact, as that's how the rest of the platform works
// for parent/child links." So an edge here applies on EVERY route: the tree is
// transitive, so Asset has seven descendants (see the pinned table in
// `hierarchy.test.ts`), and `classifyLink` turns a descendant into a
// `parent_child` link on the GENERIC Linked Items route as well as the create
// route. `node_ancestor_insert` walks exactly those rows and the perm_v2
// parent-grant cascade reads `node_ancestor` — so a grant on the Asset
// reaches a linked Issue or Action and its whole subtree, exactly as it does
// for Risk, Control, Obligation, Document, ThirdParty, InternalAuditReport
// and OversightAssessment, which have always cascaded grants from the same
// modal.
//
// Two things worth stating plainly, because neither is obvious:
//   * An edge here changes V2 too, not just V3. `packages/rest-api/src/
//     handlers/linked-item/post.ts` imports this same `link-service`, and
//     `node_ancestor_insert_refresh_trigger` is a database trigger on
//     `risksmart.linked_item`, so it fires whoever writes the row.
//   * A transitive descendant with no `*_parent` table (e.g. Asset↔cause)
//     still classifies `parent_child` but cannot be persisted — `linkItems`
//     rejects it with `LinkServiceValidationError`, which every linked-items
//     route maps to a 400. The pinned no-parent-table sets in
//     `hierarchy.test.ts` list those pairs.
//
// Do not add a parent edge here without re-reading the above: an edge is a
// permission, on both routes, on both stacks.
const AssetHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Asset,
  children: [ActionHierarchy, IssueHierarchy],
};

// COI-671 — the seven legacy issue-subtype node types, as Action parents.
//
// V2's `actions/post.ts` allow-list has always accepted them; V3's did not, and
// they were absent from this tree entirely, so `classifyLink` never returned
// `parent_child` for them and `POST /actions` answered 400. Production holds
// **871 `action_parent` rows under `issue_risk_event`** (one org), so the
// workflow is live on V2 and would break for that org on V3.
//
// THIS IS A PERMISSION CHANGE, and a deliberate one (COI-671 option 1, signed
// off; reaffirmed by the COI-666 product ruling of 2026-08-04 — after-the-fact
// parent/child links grant by design, on the generic Linked-Items route as
// well as the create route). Read the AssetHierarchy note above first: an edge
// here is transitive,
// `linkItems` writes a `_parent` row, `node_ancestor_insert` walks it and the
// perm_v2 grant cascade reads `node_ancestor` — so a grant on one of these
// nodes now reaches the actions beneath it and their updates. It changes V2
// too, because `rest-api/handlers/linked-item/post.ts` imports this same
// link-service.
//
// The edge set is deliberately MINIMAL: `[ActionHierarchy]` only, and declared
// as roots rather than nested under Risk / Control / Document / Assessment the
// way `IssueHierarchy` is. These nodes are issues, so mirroring
// `IssueHierarchy` in full (Cause, Consequence, IssueAssessment, IssueUpdate)
// and hanging them off every issue parent would have been the "consistent"
// move — and would have widened the grant surface far beyond the one workflow
// that is actually broken. Only Action needs this: V2's issue, control and
// indicator allow-lists all omit the subtypes, so nothing else can be created
// under one.
//
// The exact surface added is pinned by `hierarchy.test.ts`'s transitive
// descendant table — seven new entries, each exactly `['action',
// 'action_update']`. If that diff ever grows, the widening grew with it.
//
// These can be deleted once a migration rewrites the node ObjectTypes to
// `issue`; see COI-671 option 2.
const LegacyIssueSubtypeHierarchies: TreeModel.Model<ObjectType>[] = [
  ParentTypes.IssueRiskEvent,
  ParentTypes.IssueBreachLog,
  ParentTypes.IssueConsumerDuty,
  ParentTypes.IssueCustomerTrust,
  ParentTypes.IssueGdprBreachLog,
  ParentTypes.IssuePciBreachLog,
  ParentTypes.IssueSarLog,
].map((id) => ({ id, children: [ActionHierarchy] }));

/**
 * The declared tree, before `tree-model` wraps it.
 *
 * Exported for the canonical-subtree invariant test in `hierarchy.test.ts`, which
 * has to walk EVERY occurrence of a type id. `isDescendant` cannot serve that
 * purpose: it resolves each id to the first pre-order DFS match and so is blind,
 * by construction, to a second declaration of the same id with a different child
 * set — the defect the invariant exists to catch.
 *
 * Not part of the module's runtime contract; treat `isDescendant` as the API.
 */
export const HierarchyRootModel: TreeModel.Model<ObjectType> = {
  id: 'root',
  children: [
    ...LegacyIssueSubtypeHierarchies,
    EntityHierarchy,
    AssetHierarchy,
    RiskHierarchy,
    ThirdPartyHierarchy,
    QuestionnaireTemplateHierarchy,
    ObligationHierarchy,
    DocumentHierarchy,
    ControlGroupHierarchy,
    AssessmentHierarchy,
    InternalAuditReportHierarchy,
    InternalAuditEntityHierarchy,
    OversightAssessmentHierarchy,
  ],
};

const Root = tree.parse(HierarchyRootModel);

export const isDescendant = (
  ancestorObjectType: ParentType,
  descendantObjectType: ParentType
): boolean => {
  const ancestorNode = Root.first(
    (node) => node.model.id === ancestorObjectType
  );
  if (!ancestorNode) {
    return false;
  }
  const descendantNode = ancestorNode.first((node) => {
    return node.model.id === descendantObjectType;
  });

  return !!descendantNode && descendantNode !== ancestorNode;
};
